/**
 * Getypeerde toegang tot `/residences/:id/availability`: de agenda van één woning
 * en de blokkades die de verhuurder er zelf op zet.
 *
 * De routes bestaan sinds stage 1; het beheerscherm erbij ontbrak. Dat is geen
 * detail: `residence_night_ledger` telt eigen blokkades apart, en een regel kan
 * bepalen of ze meetellen voor een limiet (§17.28). Zonder een manier om ze te
 * plaatsen, is die hele tak dood.
 *
 * Wat hier bewust NIET gebeurt: reserveringen aanpassen. Een boeking wordt
 * geannuleerd op het reserveringenscherm, niet uit de agenda geveegd — de API
 * weigert dat ook, en het scherm hoort die grens te tonen in plaats van hem te
 * ontdekken.
 */
import { call, isRecord, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type AvailabilityFailure = Failure
export type AvailabilityResult<T> = Result<T>

/** Exact `stays.kind` (migratie 0005 en 0018). */
export const STAY_KINDS = ['booking', 'owner_block', 'maintenance', 'channel'] as const
export type StayKind = (typeof STAY_KINDS)[number]

/** De twee soorten die de verhuurder zelf mag plaatsen. */
export const BLOCK_KINDS = ['owner_block', 'maintenance'] as const
export type BlockKind = (typeof BLOCK_KINDS)[number]

export const STAY_STATUSES = ['hold', 'confirmed', 'blocked', 'cancelled', 'expired'] as const
export type StayStatus = (typeof STAY_STATUSES)[number]

export interface Stay {
  readonly id: string
  readonly residenceId: string
  /** Half-open: `checkOut` zelf is weer vrij. */
  readonly checkIn: string
  readonly checkOut: string
  readonly kind: StayKind
  readonly status: StayStatus
  readonly note: string
}

function isOneOf<T extends string>(value: unknown, known: readonly T[]): value is T {
  return typeof value === 'string' && (known as readonly string[]).includes(value)
}

function readStay(value: unknown): Stay | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const checkIn = readText(value, 'checkIn')
  const checkOut = readText(value, 'checkOut')
  const kind = value['kind']
  const status = value['status']
  // Een periode met een onbekende soort of status wordt overgeslagen en niet als
  // "overig" getekend: een dag verkeerd inkleuren in een agenda is erger dan een
  // dag niet inkleuren.
  if (
    id === '' ||
    checkIn === '' ||
    checkOut === '' ||
    !isOneOf(kind, STAY_KINDS) ||
    !isOneOf(status, STAY_STATUSES)
  ) {
    return null
  }
  return {
    id,
    checkIn,
    checkOut,
    kind,
    status,
    residenceId: readText(value, 'residenceId'),
    note: readText(value, 'note'),
  }
}

export async function listAvailability(
  residenceId: string,
  range: { readonly from: string; readonly to: string },
  options: RequestOptions = {},
): Promise<AvailabilityResult<readonly Stay[]>> {
  const query = `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
  return call(
    'GET',
    `/residences/${encodeURIComponent(residenceId)}/availability${query}`,
    undefined,
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['stays'])) {
        return null
      }
      const stays: Stay[] = []
      for (const entry of payload['stays']) {
        const stay = readStay(entry)
        if (stay !== null) {
          stays.push(stay)
        }
      }
      return stays
    },
    options,
  )
}

export async function createBlock(
  residenceId: string,
  input: {
    readonly checkIn: string
    readonly checkOut: string
    readonly kind: BlockKind
    readonly note: string
  },
): Promise<AvailabilityResult<Stay>> {
  return call(
    'POST',
    `/residences/${encodeURIComponent(residenceId)}/availability`,
    input,
    (payload) => (isRecord(payload) ? readStay(payload['stay']) : null),
  )
}

export async function removeBlock(
  residenceId: string,
  stayId: string,
): Promise<AvailabilityResult<true>> {
  return call(
    'DELETE',
    `/residences/${encodeURIComponent(residenceId)}/availability/${encodeURIComponent(stayId)}`,
    undefined,
    () => true,
  )
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function availabilityErrorMessageKey(failure: AvailabilityFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.availability.error.rejected'
    case 'conflict':
      // De enige die de verhuurder werkelijk iets zegt: de periode is al bezet.
      return 'dashboard.availability.error.occupied'
    case 'not_found':
      return 'dashboard.availability.error.notFound'
    case 'forbidden':
      return 'dashboard.availability.error.forbidden'
    default:
      return 'dashboard.availability.error.unavailable'
  }
}
